import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  Package, 
  Truck, 
  MapPin, 
  Calendar,
  Clock,
  Mail,
  Phone,
  Copy,
  ArrowRight,
  ShoppingBag,
  Home
} from 'lucide-react';
import { useStoreInfo } from '@/lib/store';
import { toast } from 'react-hot-toast';

export function OrderCompletePage() {
  const location = useLocation();
  const { storeInfo } = useStoreInfo();
  const { orderId, trackingId, customerEmail, customerName } = location.state || {};
  const [copied, setCopied] = useState(false);

  const handleCopyTracking = () => {
    if (trackingId) {
      navigator.clipboard.writeText(trackingId);
      setCopied(true);
      toast.success('Tracking ID copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const orderDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const estimatedDelivery = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  if (!orderId) {
    return (
      <>
        <Helmet>
          <title>Order Complete - {storeInfo?.site_name || 'Store'}</title>
        </Helmet>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 pb-24 md:pb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center"
          >
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Package className="h-10 w-10 text-gray-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">No Order Found</h1>
            <p className="text-gray-600 mb-8">
              It looks like you accessed this page directly. Please complete your order first.
            </p>
            <Link
              to="/cart"
              className="inline-flex items-center gap-2 bg-brand-500 hover:bg-brand-600 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
            >
              Go to Cart
              <ArrowRight className="h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Order Complete - {storeInfo?.site_name || 'Store'}</title>
        <meta name="description" content="Your order has been successfully placed" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 py-8 px-4 pb-24 md:pb-8">
        <div className="max-w-4xl mx-auto">
          {/* Success Header */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', duration: 0.6 }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full mb-6 shadow-2xl">
              <CheckCircle className="h-14 w-14 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3 font-display">
              Order Confirmed!
            </h1>
            <p className="text-xl text-gray-600">
              Thank you for your purchase, {customerName || 'valued customer'}!
            </p>
          </motion.div>

          {/* Main Content Card */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl shadow-2xl overflow-hidden"
          >
            {/* Order Status Timeline */}
            <div className="bg-gradient-to-r from-brand-500 to-brand-600 p-6 text-white">
              <h2 className="text-2xl font-bold mb-6">Order Status</h2>
              <div className="flex items-center justify-between relative">
                {/* Progress Line */}
                <div className="absolute top-5 left-0 right-0 h-1 bg-white/30">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '33%' }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="h-full bg-white"
                  />
                </div>

                {/* Steps */}
                <div className="flex-1 flex flex-col items-center relative z-10">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3 }}
                    className="w-10 h-10 bg-white rounded-full flex items-center justify-center mb-2"
                  >
                    <CheckCircle className="h-6 w-6 text-green-500" />
                  </motion.div>
                  <span className="text-sm font-medium">Order Placed</span>
                </div>

                <div className="flex-1 flex flex-col items-center relative z-10">
                  <div className="w-10 h-10 bg-white/30 rounded-full flex items-center justify-center mb-2">
                    <Package className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-sm font-medium opacity-70">Processing</span>
                </div>

                <div className="flex-1 flex flex-col items-center relative z-10">
                  <div className="w-10 h-10 bg-white/30 rounded-full flex items-center justify-center mb-2">
                    <Truck className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-sm font-medium opacity-70">Shipped</span>
                </div>

                <div className="flex-1 flex flex-col items-center relative z-10">
                  <div className="w-10 h-10 bg-white/30 rounded-full flex items-center justify-center mb-2">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-sm font-medium opacity-70">Delivered</span>
                </div>
              </div>
            </div>

            {/* Order Details */}
            <div className="p-6 md:p-8">
              {/* Tracking Information */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Package className="h-5 w-5 text-brand-500" />
                  Tracking Information
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 bg-white rounded-xl">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Tracking ID</p>
                      <p className="text-lg font-bold text-gray-900 font-mono">{trackingId}</p>
                    </div>
                    <button
                      onClick={handleCopyTracking}
                      className="p-3 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Copy tracking ID"
                    >
                      {copied ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <Copy className="h-5 w-5 text-gray-600" />
                      )}
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-white rounded-xl">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Order ID</p>
                      <p className="text-lg font-bold text-gray-900">#{orderId}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Summary Grid */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Order Date */}
                <div className="bg-gray-50 rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-brand-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Order Date</p>
                      <p className="font-semibold text-gray-900">{orderDate}</p>
                    </div>
                  </div>
                </div>

                {/* Estimated Delivery */}
                <div className="bg-gray-50 rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Clock className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Estimated Delivery</p>
                      <p className="font-semibold text-gray-900">{estimatedDelivery}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              {customerEmail && (
                <div className="bg-gray-50 rounded-2xl p-5 mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Confirmation Sent To</h3>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
                      <Mail className="h-5 w-5 text-brand-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email Address</p>
                      <p className="font-semibold text-gray-900">{customerEmail}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Important Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6">
                <div className="flex gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-900 mb-1">What's Next?</h4>
                    <p className="text-sm text-amber-800">
                      We've sent a confirmation email with your order details. You can track your order status using the tracking ID above.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid md:grid-cols-2 gap-4">
                <Link
                  to={`/track?id=${trackingId}`}
                  className="flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 text-white font-semibold py-4 rounded-xl transition-colors shadow-lg hover:shadow-xl"
                >
                  <Package className="h-5 w-5" />
                  Track Your Order
                </Link>
                <Link
                  to="/products"
                  className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-900 font-semibold py-4 rounded-xl transition-colors border-2 border-gray-200"
                >
                  <ShoppingBag className="h-5 w-5" />
                  Continue Shopping
                </Link>
              </div>

              {/* Store Contact */}
              {storeInfo && (
                <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                  <p className="text-sm text-gray-600 mb-2">Need help with your order?</p>
                  <div className="flex items-center justify-center gap-4 text-sm">
                    {storeInfo.contact_email && (
                      <a
                        href={`mailto:${storeInfo.contact_email}`}
                        className="text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1"
                      >
                        <Mail className="h-4 w-4" />
                        Email Us
                      </a>
                    )}
                    {storeInfo.phone_num && (
                      <a
                        href={`tel:${storeInfo.phone_num}`}
                        className="text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1"
                      >
                        <Phone className="h-4 w-4" />
                        Call Us
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Back to Home Link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center mt-8"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium transition-colors"
            >
              <Home className="h-5 w-5" />
              Back to Home
            </Link>
          </motion.div>
        </div>
      </div>
    </>
  );
}
